/* eslint-disable @typescript-eslint/no-explicit-any */
import { faTrash } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { AxiosError } from 'axios';
import { useBranchContext } from 'providers/BranchProvider';
import React, { useState } from 'react';
import { Button, Form, Table } from 'react-bootstrap';
import { toast } from 'react-toastify';
import axiosInstance from 'utils/axiosInterceptors';
import { UnitOfMeasure } from 'utils/enums';
import { toastConfig } from 'utils/toastConfig';
import { TSalesInvoiceItems, TUnitSellingPrice } from 'utils/types';
import { unitOptions, getUnitLabel } from 'utils/unitOptions';
import { v4 as uuidv4 } from 'uuid';
import {
  buildUpsertSalesInvoiceItemPayload,
  lineAmount
} from './salesInvoiceItemPayload';

export { unitOptions };

const priceForUnit = (
  sellingPrices: TUnitSellingPrice[] | undefined,
  unitOfMeasure: number,
  fallback?: number
) => {
  const match = sellingPrices?.find(p => p.unitOfMeasure === unitOfMeasure);
  return match?.sellingPrice ?? fallback ?? 0;
};

const optionsForItem = (item: TSalesInvoiceItems) => {
  if (item.sellingPrices && item.sellingPrices.length > 0) {
    return item.sellingPrices.map(p => ({
      value: p.unitOfMeasure,
      label: getUnitLabel(p.unitOfMeasure)
    }));
  }
  return unitOptions;
};

interface CreateSalesInvoiceTableProps {
  items: TSalesInvoiceItems[];
  setItems: React.Dispatch<React.SetStateAction<TSalesInvoiceItems[]>>;
  salesInvoiceId: number;
  isSalesInvoiceCreated: boolean;
  isLoading: boolean;
  handleSubmitSales: () => Promise<void>;
  setSubmitTrigger: React.Dispatch<React.SetStateAction<number | null>>;
  isSubmittingItems: boolean;
}

const CreateSalesInvoiceTable = ({
  items,
  setItems,
  salesInvoiceId,
  isSalesInvoiceCreated,
  isLoading,
  handleSubmitSales,
  setSubmitTrigger,
  isSubmittingItems
}: CreateSalesInvoiceTableProps) => {
  const { selectedBranch } = useBranchContext();
  const [payment, setPayment] = useState(0);
  const totalSum = items.reduce((sum, item) => sum + lineAmount(item), 0);
  const change = payment != 0 ? payment - totalSum : 0;

  const updateItemField = (
    index: number,
    key: keyof TSalesInvoiceItems,
    value: number
  ) => {
    setItems(prevItems => {
      const updatedItems = [...prevItems];
      updatedItems[index] = { ...updatedItems[index], [key]: value };
      updatedItems[index].total =
        updatedItems[index].quantity * updatedItems[index].unitPrice;
      return updatedItems;
    });
  };

  const handleSelectItem = (
    itemIndex: number,
    overrides?: Partial<TSalesInvoiceItems>
  ) => {
    void handleSubmitEditedProduct(itemIndex, overrides);
  };

  const handleSubmitEditedProduct = async (
    itemIndex: number,
    overrides?: Partial<TSalesInvoiceItems>
  ) => {
    const item = { ...items[itemIndex], ...overrides };
    if (!item || !isSalesInvoiceCreated || !item.isSubmitted) return;
    if (!item.salesInvoiceItemId) return;

    try {
      await axiosInstance.put(
        `/api/v1/${selectedBranch?.branchDomain}/salesInvoices/${salesInvoiceId}/invoiceItems/${item.salesInvoiceItemId}`,
        buildUpsertSalesInvoiceItemPayload(item)
      );
      toast.success('Item updated successfully!', { ...toastConfig });
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(
          error.response?.data?.errorMessage || 'Failed to update item.',
          { ...toastConfig }
        );
      }
    }
  };

  const handleDeleteSalesInvoiceItem = async (itemIndex: number) => {
    const item = items[itemIndex];
    if (!item || !item.salesInvoiceItemId) return false;

    try {
      await axiosInstance.delete(
        `/api/v1/${selectedBranch?.branchDomain}/salesInvoices/${salesInvoiceId}/invoiceItems/${item.salesInvoiceItemId}`
      );
      toast.success('Item Deleted', { ...toastConfig, autoClose: 2000 });
      return true;
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data.errorMessage, {
          ...toastConfig,
          autoClose: 3000
        });
      }
      return false;
    }
  };

  const handleBarcodeChange = async (barcode: string, index: number) => {
    if (!barcode) {
      setItems(prevItems => {
        const newItems = [...prevItems];
        newItems[index] = {
          ...newItems[index],
          procurementInvoiceId: 0,
          productId: 0,
          productName: '',
          unitPrice: 0,
          total: 0,
          salesInvoiceItemId: 0,
          noIdProductName: ''
        };
        return newItems;
      });
      return;
    }

    try {
      const res = await axiosInstance.get(
        `/api/v1/${selectedBranch?.branchDomain}/products/byBarcode?barcode=${barcode}`
      );
      const { data } = res;
      const defaultUom =
        data.defaultSellUnitOfMeasure ??
        data.stockUnitOfMeasure ??
        UnitOfMeasure.Piece;
      const unitPrice = priceForUnit(
        data.sellingPrices,
        defaultUom,
        data.sellingPrice
      );

      setItems(prevItems => {
        const updatedItems = [...prevItems];
        updatedItems[index] = {
          ...updatedItems[index],
          id: updatedItems[index].id || uuidv4(),
          procurementInvoiceId: data.procurementInvoiceId,
          productId: data.productId,
          productName: data.name,
          noIdProductName: '',
          unitOfMeasure: defaultUom,
          unitPrice,
          sellingPrices: data.sellingPrices,
          stockUnitOfMeasure: data.stockUnitOfMeasure,
          packSize: data.packSize,
          quantityOnHand: data.quantityOnHand,
          total: (updatedItems[index].quantity || 1) * unitPrice
        };
        return updatedItems;
      });
      if (!isSubmittingItems) {
        setSubmitTrigger(index); // Set a trigger when barcode data is updated
      }
    } catch (error) {
      if (error instanceof AxiosError) {
        toast.error(error.response?.data.errorMessage, {
          ...toastConfig,
          autoClose: 3000
        });
      }
    }
  };

  const handleProductNameChange = (value: string, index: number) => {
    const item = items[index];
    const trimmed = value.trim();
    const catalogName = (item.productName || '').trim();
    if (item.productId && (trimmed === '' || trimmed === catalogName)) {
      return;
    }

    setItems(prevItems => {
      const updatedItems = [...prevItems];
      updatedItems[index] = {
        ...updatedItems[index],
        noIdProductName: value,
        productId: null,
        procurementInvoiceId: null
      };
      return updatedItems;
    });
    if (!isSubmittingItems) {
      setSubmitTrigger(index);
    }
  };

  const updateProductNameLocally = (value: string, index: number) => {
    setItems(prevItems => {
      const updatedItems = [...prevItems];
      updatedItems[index].noIdProductName = value;
      return updatedItems;
    });
  };

  const addRow = () => {
    setItems(prevItems => [
      ...prevItems,
      {
        productId: 0,
        procurementInvoiceId: 0,
        quantity: 1,
        unitOfMeasure: UnitOfMeasure.Piece,
        unitPrice: 0,
        productName: '',
        total: 0,
        salesInvoiceItemId: 0,
        noIdProductName: '',
        id: uuidv4(), // used uuid to generate unique id to set and compare the value to support no barcode product items
        isDiscountable: false
      }
    ]);
  };

  const deleteRow = async (index: number) => {
    const item = items[index];
    if (item.salesInvoiceItemId) {
      const success = await handleDeleteSalesInvoiceItem(index);
      if (success) {
        setItems(prevItems => prevItems.filter((_, idx) => idx !== index));
      }
    } else {
      setItems(prevItems => prevItems.filter((_, idx) => idx !== index));
    }
  };

  return (
    <>
      <Table responsive="sm">
        <thead>
          <tr>
            <th>Quantity</th>
            <th>Unit Of Measure</th>
            <th>Barcode</th>
            <th>Product Name</th>
            <th>Unit Price</th>
            <th>Total</th>
          </tr>
        </thead>
        <tbody>
          {isSalesInvoiceCreated &&
            items?.map((item, index) => (
              <tr key={index}>
                <td>
                  <Form.Control
                    className="p-2"
                    type="number"
                    placeholder="Quantity"
                    value={item.quantity}
                    onChange={e =>
                      updateItemField(index, 'quantity', Number(e.target.value))
                    }
                    onBlur={() => handleSelectItem(index)}
                  />
                </td>
                <td>
                  <Form.Select
                    value={item.unitOfMeasure}
                    onChange={e => {
                      const nextUom = Number(e.target.value);
                      const nextPrice = priceForUnit(
                        item.sellingPrices,
                        nextUom,
                        item.unitPrice
                      );
                      setItems(prevItems => {
                        const updatedItems = [...prevItems];
                        updatedItems[index] = {
                          ...updatedItems[index],
                          unitOfMeasure: nextUom,
                          unitPrice: nextPrice,
                          total: updatedItems[index].quantity * nextPrice
                        };
                        return updatedItems;
                      });
                    }}
                    onBlur={() => handleSelectItem(index)}
                  >
                    {optionsForItem(item).map(option => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </Form.Select>
                  {item.quantityOnHand != null && item.stockUnitOfMeasure != null && (
                    <small className="text-muted d-block">
                      Stock: {item.quantityOnHand}{' '}
                      {getUnitLabel(item.stockUnitOfMeasure)}
                      {item.packSize
                        ? ` (≈ ${(item.quantityOnHand / item.packSize).toFixed(2)} rolls)`
                        : ''}
                    </small>
                  )}
                </td>
                <td>
                  <Form.Control
                    className="p-2"
                    placeholder="Barcode"
                    onBlur={e => handleBarcodeChange(e.target.value, index)}
                    disabled={item.isSubmitted || !!item.noIdProductName}
                  />
                </td>
                <td>
                  <Form.Control
                    className="p-2"
                    value={item.productName || item.noIdProductName || ''}
                    onBlur={e => handleProductNameChange(e.target.value, index)}
                    onChange={e =>
                      updateProductNameLocally(e.target.value, index)
                    }
                    disabled={item.isSubmitted}
                  />
                </td>
                <td>
                  <Form.Control
                    type="number"
                    className="p-2"
                    placeholder="Unit Price"
                    value={item.unitPrice}
                    onChange={e =>
                      updateItemField(
                        index,
                        'unitPrice',
                        parseFloat(e.target.value)
                      )
                    }
                    onBlur={e => {
                      const parsed = parseFloat(e.target.value);
                      const unitPrice = Number.isFinite(parsed) ? parsed : 0;
                      updateItemField(index, 'unitPrice', unitPrice);
                      handleSelectItem(index, { unitPrice });
                    }}
                  />
                </td>
                <td>
                  <Form.Control
                    className="p-2"
                    placeholder="Total"
                    value={lineAmount(item)}
                    readOnly
                  />
                </td>
                <td>
                  <FontAwesomeIcon
                    icon={faTrash}
                    style={{ cursor: 'pointer', color: '#C63C51' }}
                    onClick={() => deleteRow(index)}
                  />
                </td>
              </tr>
            ))}
        </tbody>
      </Table>

      {isSalesInvoiceCreated && (
        <Button className="mb-3" onClick={addRow} disabled={isLoading}>
          Add Item
        </Button>
      )}
      <div className="d-flex flex-column">
        <h5 className="align-self-end mb-3">
          Subtotal: {totalSum.toLocaleString()}
        </h5>
        <h5 className="align-self-end mb-3">Change: {change}</h5>
        <Form.Control
          placeholder="Enter Payment"
          style={{ width: '10rem', alignSelf: 'end', marginBottom: '1rem' }}
          onChange={e => setPayment(+e.target.value)}
        />
        <div className="d-flex flex-column align-items-end justify-content-end"></div>
        {isSalesInvoiceCreated && (
          <Button
            style={{ alignSelf: 'end', width: '10rem', marginBottom: '1rem' }}
            onClick={handleSubmitSales}
            disabled={isLoading}
          >
            SUBMIT
          </Button>
        )}
      </div>
    </>
  );
};

export default CreateSalesInvoiceTable;
